<script lang="ts" setup>
    import '../../../css/choice.css';
    import { popAlert, ModalStyles } from '../../lib/Notification';
    import { ref, reactive } from 'vue';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import { NoTzDate, getNoTzDateAdd, getNoTzToday } from "../../lib/DateUtils"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import { ICol, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { ButtonDisplaySize, ButtonDisplayStyle } from '../../Enums/UiEnums';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { ChoiceApi } from '../../LibMain';
    import { ITextToPayPatientReportData } from '../../lib/Models/Reports/TextToPayModel';

    const reportColumns = ref<ICol[]>([
        { Name: 'Chart', DataField: 'chart' },
        { Name: 'Patient Name', DataField: 'patientName' },
        { Name: 'Request Date', DataField: 'requestDate' },
        { Name: 'Amount', DataField: 'amount', RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Delivered To', DataField: 'deliveredTo', Tooltip: 'deliveredToTooltip' },
        { Name: 'Status', DataField: 'status' },
        { Name: 'User', DataField: 'user', Tooltip: 'userTooltip' }
    ]);

    const props = defineProps<{
        patientEid: number
    }>();
    const pageData = reactive<{
        Search: { 
            StartDate: NoTzDate, 
            EndDate: NoTzDate 
        },
        textToPayHistory: ITextToPayPatientReportData[]
    }>({
        Search: {
            StartDate: getNoTzDateAdd(-365),
            EndDate: getNoTzToday() 
        },
        textToPayHistory: []
    });

    function clearSearchData(): void {
        pageData.textToPayHistory = [];
    }
    async function closeWindow(): Promise<void> {
        window.close();
    }
    async function printReport(): Promise<void> {
        window.print();
    }

    async function getReportData(): Promise<void> {
        const result = await ChoiceApi.reporting.getPatientTextToPayHistory(props.patientEid, pageData.Search.StartDate, pageData.Search.EndDate);
        if (!result) {
            popAlert("Failed to get Text to Pay history.", "Text to Pay History", ModalStyles.warning);
            return;
        }
        pageData.textToPayHistory = result;
    }
</script>

<template>
    <WindowHeaderFooter :headerHeight="3.5" :footerHeight="3.5"
        currentPathOveride="officemd/App/billing/PaySimpleCreditCard">
        <template v-slot:WindowHeader>
            <div class="isalus-page-title-large pt-2 pb-2">Text to Pay Patient History</div>
        </template>
        <template v-slot:WindowBody>
            <div class="row label-isalus-small">
                <div class="d-flex">
                    <div class="label-search-details me-5">Date Range:</div>
                    <div class="me-2">
                        <DatePickerNoTZ 
                            v-model:value="pageData.Search.StartDate"
                            :clearable="false"
                            :required=true
                            :errorDisplay=false
                            :placeholder="'Search Start Date'"
                            :cssFramework="'vanilla'"
                            @update:value="() => clearSearchData()" />
                    </div>
                    <div>
                        <DatePickerNoTZ 
                            v-model:value="pageData.Search.EndDate"
                            :clearable="false"
                            :required=true
                            :errorDisplay=false
                            :placeholder="'Search End Date'"
                            :cssFramework="'vanilla'"
                            @update:value="() => clearSearchData()" />
                    </div>
                </div>
            </div>
            <p style="font-size: .75rem;" class="pt-2">
                <ChoiceTable
                    :columns="reportColumns"
                    :data="pageData.textToPayHistory"
                    :show-filters="false"
                    :show-footer="false"
                    :allowSorting="true"
                    :key-value-data-member="'recordKey'"
                />
            </p>
        </template>
        <template v-slot:WindowFooter>
            <div class="text-end ">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Search" 
                    :text="'Search'"
                    :size="ButtonDisplaySize.Small"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="getReportData"
                    :endingPadding="1" />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Print"
                    :text="'Print'"
                    :size="ButtonDisplaySize.Small"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="printReport"
                    :endingPadding="1" />
                <ActionButtonV2
                    :action="ActionButtonStyle.Close"
                    :text="'Close'"
                    :size="ButtonDisplaySize.Small"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"
                    :performAction="closeWindow" />
            </div>
        </template>
    </WindowHeaderFooter>
</template>
